import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Server, Lock, Database, FileCheck, Building2 } from "lucide-react";

export default function TechnischeDoku() {
  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-50 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur App
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-yellow-500 rounded-sm flex items-center justify-center">
              <Shield className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight text-zinc-50" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Technische Dokumentation
              </h1>
              <p className="text-zinc-400">Datensicherheit & Speicherung</p>
            </div>
          </div>
          <p className="text-sm text-zinc-500">
            Stand: {new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' })} | Version 1.0
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 space-y-12">
        
        {/* Einleitung */}
        <section>
          <h2 className="text-2xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <FileCheck className="w-6 h-6 text-yellow-500" />
            1. Einleitung
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300 leading-relaxed">
            <p>
              Das <strong className="text-yellow-500">Unfallmelde Tool</strong> ist eine webbasierte Anwendung zur 
              Erfassung und Verwaltung von Arbeitsunfällen, Beinaheunfällen und Verbandbucheinträgen. 
              Diese Dokumentation beschreibt die technischen Sicherheitsmaßnahmen und die Art der Datenspeicherung.
            </p>
            <p className="mt-4">
              <strong>Betreiber:</strong> manage4yougroup UG (haftungsbeschränkt)<br />
              <strong>Anwendungsbereich:</strong> Arbeitsschutz und Unfallprävention
            </p>
          </div>
        </section>

        {/* Datenspeicherung */}
        <section>
          <h2 className="text-2xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <Database className="w-6 h-6 text-yellow-500" />
            2. Datenspeicherung
          </h2>
          <div className="space-y-4">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
              <h3 className="text-lg font-semibold text-zinc-50 mb-3">2.1 Datenbank</h3>
              <table className="w-full text-sm">
                <tbody className="text-zinc-300">
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 text-zinc-400 w-1/3">Anbieter</td>
                    <td className="py-3">MongoDB Atlas (MongoDB, Inc.)</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 text-zinc-400">Serverstandort</td>
                    <td className="py-3">Europäische Union (konfigurierbar: Frankfurt, Irland)</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 text-zinc-400">Verschlüsselung</td>
                    <td className="py-3">AES-256 (at rest) + TLS 1.2/1.3 (in transit)</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-zinc-400">Zertifizierungen</td>
                    <td className="py-3">ISO 27001, SOC 2 Type II, HIPAA, PCI DSS</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
              <h3 className="text-lg font-semibold text-zinc-50 mb-3">2.2 Dateispeicherung (Bilder)</h3>
              <table className="w-full text-sm">
                <tbody className="text-zinc-300">
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 text-zinc-400 w-1/3">Anbieter</td>
                    <td className="py-3">Emergent Object Storage (Cloud-basiert)</td>
                  </tr>
                  <tr className="border-b border-zinc-800">
                    <td className="py-3 text-zinc-400">Dateitypen</td>
                    <td className="py-3">Bilder/Fotos zu Unfallmeldungen (JPEG, PNG)</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-zinc-400">Zugriff</td>
                    <td className="py-3">Nur authentifizierte Benutzer mit Berechtigung</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Gespeicherte Daten */}
        <section>
          <h2 className="text-2xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <Server className="w-6 h-6 text-yellow-500" />
            3. Gespeicherte Daten
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
            <h3 className="text-lg font-semibold text-zinc-50 mb-3">3.1 Meldungsarten</h3>
            <p className="text-zinc-300 mb-4">Das System unterscheidet drei Arten von Meldungen:</p>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                <h4 className="font-semibold text-yellow-500 mb-2">Verbandbucheintrag</h4>
                <p className="text-zinc-400 text-sm">Dokumentation von Erste-Hilfe-Maßnahmen und kleineren Verletzungen gemäß DGUV Vorschrift 1</p>
              </div>
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                <h4 className="font-semibold text-yellow-500 mb-2">Beinaheunfall</h4>
                <p className="text-zinc-400 text-sm">Ereignisse ohne Personenschaden, die zu einem Unfall hätten führen können</p>
              </div>
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                <h4 className="font-semibold text-yellow-500 mb-2">Arbeitsunfall</h4>
                <p className="text-zinc-400 text-sm">Meldepflichtige Unfälle gemäß § 193 SGB VII (ab 3 Tagen Arbeitsunfähigkeit)</p>
              </div>
            </div>

            <h3 className="text-lg font-semibold text-zinc-50 mb-3">3.2 Datenfelder je Meldung</h3>
            <ul className="text-zinc-300 space-y-2 list-disc list-inside">
              <li><strong>Meldungsart:</strong> Verbandbucheintrag, Beinaheunfall oder Arbeitsunfall</li>
              <li><strong>Abteilung/Ort:</strong> Bereich des Vorfalls (z.B. Produktion, Lager, Verwaltung)</li>
              <li><strong>Datum:</strong> Zeitpunkt des Vorfalls</li>
              <li><strong>Verletztes Körperteil:</strong> Betroffene Körperregion</li>
              <li><strong>Art der Verletzung:</strong> z.B. Schnittverletzung, Prellung, Verbrennung</li>
              <li><strong>Ursache:</strong> Kategorisiert nach 8 Ursachengruppen (Menschliches Verhalten, Arbeitsmittel, Technische Mängel, etc.)</li>
              <li><strong>Wegeunfall:</strong> Kennzeichnung ob Unfall auf dem Arbeitsweg</li>
              <li><strong>Behandlung:</strong> Ob medizinische Behandlung erfolgte</li>
              <li><strong>Arbeit eingestellt:</strong> Ob die Arbeit unterbrochen wurde</li>
              <li><strong>Fehltage:</strong> Anzahl der Arbeitsunfähigkeitstage</li>
              <li><strong>Beschreibung:</strong> Freitext-Beschreibung des Vorfalls</li>
              <li><strong>Name des Verunfallten:</strong> Personenbezogene Daten der betroffenen Person</li>
              <li><strong>Zeugen:</strong> Namen anwesender Personen</li>
              <li><strong>Fotodokumentation:</strong> Optional hochgeladene Bilder</li>
              <li><strong>Erstellt von:</strong> Name des Erfassers</li>
              <li><strong>Erstellungszeitpunkt:</strong> Automatischer Zeitstempel</li>
              <li><strong>Unternehmenszuordnung:</strong> Zugehörigkeit zum Mandanten (Multi-Tenant)</li>
            </ul>

            <h3 className="text-lg font-semibold text-zinc-50 mb-3 mt-6">3.3 Benutzerdaten</h3>
            <ul className="text-zinc-300 space-y-2 list-disc list-inside">
              <li>E-Mail-Adresse (Anmeldung)</li>
              <li>Name</li>
              <li>Passwort (bcrypt-gehasht, nicht im Klartext)</li>
              <li>Unternehmenszuordnung</li>
              <li>Berechtigungsstufe (Super-Admin / Unternehmens-Benutzer)</li>
            </ul>

            <h3 className="text-lg font-semibold text-zinc-50 mb-3 mt-6">3.4 Unternehmensdaten</h3>
            <ul className="text-zinc-300 space-y-2 list-disc list-inside">
              <li>Unternehmensname</li>
              <li>Erstellungszeitpunkt</li>
              <li>Zugeordnete Benutzer</li>
            </ul>
          </div>
        </section>

        {/* Sicherheitsmaßnahmen */}
        <section>
          <h2 className="text-2xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <Lock className="w-6 h-6 text-yellow-500" />
            4. Sicherheitsmaßnahmen
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
              <h3 className="text-lg font-semibold text-zinc-50 mb-3">Transportverschlüsselung</h3>
              <p className="text-zinc-300 text-sm">
                Alle Daten werden ausschließlich über HTTPS (TLS 1.2/1.3) übertragen. 
                Unverschlüsselte HTTP-Verbindungen werden automatisch umgeleitet.
              </p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
              <h3 className="text-lg font-semibold text-zinc-50 mb-3">Authentifizierung</h3>
              <p className="text-zinc-300 text-sm">
                JWT-basierte Authentifizierung mit sicheren Token. 
                Passwörter werden mit bcrypt (Kostenfaktor 12) gehasht.
              </p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
              <h3 className="text-lg font-semibold text-zinc-50 mb-3">Datentrennung</h3>
              <p className="text-zinc-300 text-sm">
                Multi-Tenant-Architektur: Jedes Unternehmen kann nur auf eigene Daten zugreifen. 
                Technische Trennung durch Unternehmens-ID.
              </p>
            </div>
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6">
              <h3 className="text-lg font-semibold text-zinc-50 mb-3">Backups</h3>
              <p className="text-zinc-300 text-sm">
                Automatische tägliche Backups durch MongoDB Atlas. 
                Point-in-Time-Recovery für die letzten 7 Tage möglich.
              </p>
            </div>
          </div>
        </section>

        {/* Multi-Tenant */}
        <section>
          <h2 className="text-2xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <Building2 className="w-6 h-6 text-yellow-500" />
            5. Mandantentrennung (Multi-Tenancy)
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300 leading-relaxed">
            <p>
              Das System verwendet eine <strong className="text-yellow-500">logische Mandantentrennung</strong>. 
              Jeder Datensatz ist einem Unternehmen zugeordnet (company_id).
            </p>
            <ul className="mt-4 space-y-2 list-disc list-inside">
              <li>Benutzer sehen ausschließlich Daten ihres eigenen Unternehmens</li>
              <li>API-Anfragen werden serverseitig gefiltert</li>
              <li>Keine Möglichkeit, auf fremde Unternehmensdaten zuzugreifen</li>
              <li>Administratoren haben unternehmensübergreifende Rechte nur nach expliziter Zuweisung</li>
            </ul>
          </div>
        </section>

        {/* DSGVO */}
        <section>
          <h2 className="text-2xl font-bold text-zinc-50 mb-4 flex items-center gap-3">
            <Shield className="w-6 h-6 text-yellow-500" />
            6. DSGVO-Konformität
          </h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300 leading-relaxed">
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-yellow-500 font-bold">✓</span>
                <span><strong>Datenminimierung:</strong> Es werden nur für den Zweck erforderliche Daten erhoben</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-yellow-500 font-bold">✓</span>
                <span><strong>Zweckbindung:</strong> Daten werden ausschließlich für Arbeitsschutz-Dokumentation verwendet</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-yellow-500 font-bold">✓</span>
                <span><strong>Speicherort EU:</strong> MongoDB Atlas Rechenzentren in der EU konfigurierbar</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-yellow-500 font-bold">✓</span>
                <span><strong>Auskunftsrecht:</strong> Export aller Daten jederzeit per CSV möglich</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-yellow-500 font-bold">✓</span>
                <span><strong>Löschrecht:</strong> Einzelne Meldungen können gelöscht werden</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-yellow-500 font-bold">✓</span>
                <span><strong>Auftragsverarbeitung:</strong> MongoDB, Inc. ist als Auftragsverarbeiter DSGVO-konform</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Kontakt */}
        <section>
          <h2 className="text-2xl font-bold text-zinc-50 mb-4">7. Kontakt</h2>
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-zinc-300">
            <p>
              Bei Fragen zur Datensicherheit oder für Anfragen nach Art. 15-21 DSGVO:
            </p>
            <p className="mt-4">
              <strong className="text-zinc-50">manage4yougroup UG (haftungsbeschränkt)</strong><br />
              Am Kahlberg 8<br />
              91093 Heßdorf<br />
              Deutschland<br /><br />
              Telefon: 09135 7189445<br />
              E-Mail: sebastian.hotz@manage4yougroup.de
            </p>
          </div>
        </section>

        {/* Footer */}
        <div className="border-t border-zinc-800 pt-8 text-center text-zinc-500 text-sm">
          <p>Dieses Dokument dient zur Information über die technische Umsetzung der Datenspeicherung.</p>
          <p className="mt-2">© {new Date().getFullYear()} manage4yougroup UG (haftungsbeschränkt)</p>
        </div>
      </div>
    </div>
  );
}
